import { yupResolver } from "@hookform/resolvers/yup";
import { Delete } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import facilityService from "../../services/facility";
import service from "../../services/room";
import queryDefault from "../../utils/queryDefault";

interface FormValues {
  property_id: number,
  name: String,
  description: String,
  daily_price: number,
  monthly_price: number,
  half_yearly_price: number,
  yearly_price: number,
  admin_name: String[],
  room_facilities: any[],
  photos: File[],
  type: String,
};

export default function RoomCreate() {
  const { id } = useParams();
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const [photos, setPhotos] = useState<File[]>([]);

  const schema = yup.object().shape({
    name: yup.string().min(2).required("Name is a required field and length > 2"),
  });
  const form = useForm({
    defaultValues: {
      property_id: id,
      name: "",
      description: "",
      daily_price: 0,
      monthly_price: 0,
      half_yearly_price: 0,
      yearly_price: 0,
      admin_name: user.username,
      room_facilities: [],
      photos: [],
      type: "male",
    },
    resolver: yupResolver(schema),
  });

  const createRoom = useMutation(
    "create_room",
    service.createRoom,
    {
      onSuccess: () => {
        navigate(`/app/properties/property/detail/${id}`);

        notification.onOpen({
          message: "Room was successfully created!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setImages: React.Dispatch<React.SetStateAction<File[]>>,
    field: keyof FormValues
  ) => {
    if (event.target.files) {
      const fileList = Array.from(event.target.files);
      setImages((prevImages) => [...prevImages, ...fileList]);
      const currentValues = form.getValues(field) as File[];
      form.setValue(field, [...currentValues, ...fileList] as any);
    }
  };

  const handleDelete = (
    index: number,
    setImages: React.Dispatch<React.SetStateAction<File[]>>,
    images: File[],
    field: keyof FormValues
  ) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    form.setValue(field, updatedImages);
  };

  const listFacility = useQuery("list-facility", () => facilityService.getAllFacility({ keyword: "", type: "Room" }));

  const onSubmit = (data: any) => {
    if (
      photos.length > 10
    ) {
      notification.onOpen({
        message: "Cannot upload image more than 10",
        type: "error",
        position: "top",
      });
      return;
    }
    const formData = new FormData();
    photos.forEach((photo) => {
      formData.append("room", photo);
    });

    Object.keys(data).forEach((key) => {
      if (!["photos"].includes(key)) {
        formData.append(key, data[key]);
      }
    });
    createRoom.mutate(formData);
  };

  const [selectedElements, setSelectedElements] = useState<any>([]);

  const onSelectionChange = (event: any) => {
    const value = event.target.value;
    form.setValue("room_facilities", value);
    setSelectedElements(value);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Create Room"
        breadcrumbs={[
          { label: "Properties", href: "/app/properties/property" },
          { label: "Property", href: `/app/properties/property/detail/${id}` },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="property"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Description</FormLabel>
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="medium"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={"male"} key={0}>Male Only</MenuItem>
                        <MenuItem value={"female"} key={1}>Female Only</MenuItem>
                        <MenuItem value={"mix"} key={2}>Mix</MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Facility</FormLabel>
                <Controller
                  name="room_facilities"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        multiple
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        renderValue={(selected: any) => selected.map((id: any) => {
                          var facility = ""
                          listFacility?.data?.data?.forEach((element: any) => {
                            if (element.id === id) {
                              facility = element.name
                            }
                          });
                          return facility;
                        }).join(", ")}
                        onChange={onSelectionChange}
                      >
                        {listFacility?.data?.data?.map((facility: any) => (
                          <MenuItem value={facility.id} key={facility.id}>
                            <ListItemText primary={facility.name} />
                            <Checkbox checked={selectedElements.indexOf(facility.id) > -1} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Daily Price</FormLabel>
                <Controller
                  name="daily_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Monthly Price</FormLabel>
                <Controller
                  name="monthly_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Half-Yearly Price</FormLabel>
                <Controller
                  name="half_yearly_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Yearly Price</FormLabel>
                <Controller
                  name="yearly_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h3">Photos</Typography>

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item lg={6} xs={12}>
                <FormControl fullWidth margin="normal">
                  <Controller
                    name="photos"
                    control={form.control}
                    render={({ field }) => (
                      <>
                        <input
                          type="file"
                          name="room"
                          multiple
                          onChange={(event) =>
                            handleFileChange(
                              event,
                              setPhotos,
                              "photos"
                            )
                          }
                          hidden
                        />
                        <Button
                          variant="contained"
                          component="span"
                          onClick={() => {
                            let element: HTMLElement = document.querySelector(
                              'input[name="room"]'
                            )!;
                            element.click();
                          }}
                        >
                          Upload Photo
                        </Button>
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item lg={6} xs={12}>
                <List>
                  {photos.map((photo, index) => (
                    <>
                      <ListItem
                        key={index}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() =>
                              handleDelete(
                                index,
                                setPhotos,
                                photos,
                                "photos"
                              )
                            }
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={photo.name} />
                      </ListItem>
                      <img
                        src={URL.createObjectURL(photo)}
                        alt="Uploaded"
                        style={{
                          width: "100%",
                          borderRadius: "0.375rem",
                          objectFit: "cover",
                          alignSelf: "start"
                        }}
                      />
                    </>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createRoom.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
